import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import type { MenuItemContent } from '../types.js';
import { featuredItem } from './cafe-panels.js';
import {
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  price,
  sectionSubtitle,
  sectionTitle,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Pizza Showcase, "Red panel left": the same black-and-red pizzeria look as the grid design. A
 * tall red panel with a slanted edge holds the brand and the featured dish as a big round photo
 * with its prices; the rest of the menu runs in three columns beside it.
 */

const S = '.mb-t-pizza-show-panel';

/** Green, white and red bars: the Italian stripe under the brand. */
const STRIPE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 1' preserveAspectRatio='none'%3E%3Crect width='1' height='1' fill='%23169b4c'/%3E%3Crect x='1' width='1' height='1' fill='%23f4f4f4'/%3E%3Crect x='2' width='1' height='1' fill='%23d52b1e'/%3E%3C/svg%3E\")";
/** A sprig of basil beside the brand. */
const BASIL =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cpath d='M30 62C29 48 30 34 36 20' stroke='%232f6f2a' stroke-width='2.4' fill='none' stroke-linecap='round'/%3E%3Cpath d='M35 22C22 22 11 13 10 3C24 2 35 10 35 22Z' fill='%233f9b35'/%3E%3Cpath d='M35 22C26 15 19 9 10 3' stroke='%23296324' stroke-width='1.2' fill='none'/%3E%3Cpath d='M36 24C46 15 58 15 63 22C55 33 43 33 36 24Z' fill='%234fb043'/%3E%3Cpath d='M36 24C45 22 54 21 63 22' stroke='%232f7a2a' stroke-width='1.2' fill='none'/%3E%3Cpath d='M31 40C20 44 9 39 5 29C17 25 27 30 31 40Z' fill='%23388d30'/%3E%3Cpath d='M31 40C22 36 13 32 5 29' stroke='%23245c20' stroke-width='1.2' fill='none'/%3E%3Cpath d='M32 44C40 37 51 38 56 45C49 53 38 52 32 44Z' fill='%2345a33b'/%3E%3C/svg%3E\")";

function priceTable(ctx: RenderContext, item: MenuItemContent): string {
  if (item.variants.length) {
    return item.variants
      .slice(0, 3)
      .map(
        (v) =>
          `<div class="psp-row"><span class="psp-lbl">${variantLabel(ctx, v.label, v.labelAr)}</span><span class="psp-amt" dir="ltr">${price(ctx, v.price)}</span></div>`,
      )
      .join('');
  }
  const old =
    item.discountPrice !== null && item.discountPrice < item.price
      ? `<s class="psp-old" dir="ltr">${price(ctx, item.price)}</s>`
      : '';
  const now = old ? item.discountPrice! : item.price;
  return `<div class="psp-row psp-one"><span class="psp-chip"><span class="psp-amt" dir="ltr">${price(ctx, now)}</span>${old}</span></div>`;
}

function brand(ctx: RenderContext): string {
  const { board } = ctx;
  const has = board.brand.name?.trim() || board.brand.nameAr?.trim();
  const text = has
    ? bilingual(ctx, board.brand.name, board.brand.nameAr, 'psp-bname')
    : bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'psp-bname');
  return `<div class="psp-brand"><div class="psp-bwords">${text}<span class="psp-stripe"></span></div><span class="psp-basil"></span></div>`;
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="psp-head">${sectionTitle(ctx, b.section, 'psp-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'psp-sub')}</div>`
    : '';
  return `<div class="psp-sec">${head}${b.items.map((i) => listItem(ctx, i, 'pspl')).join('')}</div>`;
}

export const pizzaShowPanel: MenuTemplate = {
  id: 'pizza-show-panel',
  name: 'Pizza showcase: red panel',
  description:
    'A tall slanted red panel with your brand and the featured dish as a big round photo with its prices, and the rest of the menu in three columns on black.',
  defaultTheme: {
    backgroundColor: '#0b0b0b',
    surfaceColor: '#1b1b1b',
    textColor: '#ffffff',
    primaryColor: '#ffc72c',
    accentColor: '#d62828',
    fontId: 'bold',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'black-red', name: 'Black & red', theme: {} },
    { id: 'charcoal-green', name: 'Charcoal & green', theme: { backgroundColor: '#1d2023', surfaceColor: '#292d31', textColor: '#f3f3f1', primaryColor: '#ffd166', accentColor: '#1f7a4d' } },
    { id: 'navy-orange', name: 'Navy & orange', theme: { backgroundColor: '#0d1828', surfaceColor: '#16253b', textColor: '#f4f6fa', primaryColor: '#ffd23f', accentColor: '#e8590c' } },
    { id: 'cream-red', name: 'Cream paper & red', theme: { backgroundColor: '#f5ecdc', surfaceColor: '#fffaf0', textColor: '#2a211b', primaryColor: '#9e1b14', accentColor: '#c8102e' } },
  ],
  cuisines: ['pizza', 'pasta'],
  family: { id: 'pizza-show', name: 'Pizza Showcase', layout: 'Red panel left' },
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const featured = featuredItem(board);
    return packColumns(
      groupBySection(board, featured?.id),
      listColumns(board, {
        count: portrait ? 2 : 3,
        capacity: portrait ? 56 : 52,
        heading: 3.4,
        item: { nameEm: 1.5, descEm: 0.9, perLine: portrait ? 46 : 38, gap: 1.3 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const item = featuredItem(board);
    let hero: string;
    if (item) {
      const photo = image(ctx, item.imageMediaId, 'psp-img');
      const initial = escapeHtml((item.title.trim()[0] ?? '?').toUpperCase());
      const badges = badge(ctx, item, 'psp-badge');
      const scale = fitScale(ctx.showEn ? item.title : item.titleAr || item.title, 16, 0.6);
      hero = [
        `<div class="psp-photo"><div class="psp-ph">${photo || `<span class="psp-initial">${initial}</span>`}</div>`,
        badges ? `<span class="psp-badges">${badges}</span>` : '',
        '</div>',
        '<div class="psp-htext">',
        `<div class="psp-hnames" style="font-size:${scale.toFixed(2)}em">${bilingual(ctx, item.title, item.titleAr, 'psp-hname')}</div>`,
        description(ctx, item, 'psp-hdesc'),
        `<div class="psp-prices">${priceTable(ctx, item)}</div>`,
        '</div>',
      ].join('');
    } else {
      hero = `<div class="psp-htext">${bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'psp-tagline')}</div>`;
    }
    const panel = `<aside class="psp-panel"><div class="psp-pin">${brand(ctx)}<div class="psp-hero">${hero}</div></div></aside>`;

    const title = bilingual(ctx, board.settings.title, board.settings.titleAr, 'psp-heading');
    const blocks = ctx.page.columns.flat();
    const lists = blocks.length
      ? `<div class="psp-cols">${ctx.page.columns
          .map((c) => `<div class="psp-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
          .join('')}</div>`
      : item
        ? ''
        : '<div class="mb-empty">No dishes on this board yet</div>';
    return `<div class="psp-wrap">${panel}<div class="psp-main">${title ? `<div class="psp-top">${title}<span class="psp-rule"></span></div>` : ''}${lists}</div></div>`;
  },

  css: `
${S}{padding:0;background-image:radial-gradient(ellipse at 70% 40%,rgba(255,255,255,.05),transparent 65%)}
${S} .psp-wrap{flex:1;min-height:0;display:flex}
${S}.mb-portrait .psp-wrap{flex-direction:column}

${S} .psp-panel{position:relative;width:35%;flex-shrink:0;background:var(--mb-accent);color:#fff;
  clip-path:polygon(0 0,100% 0,84% 100%,0 100%);
  background-image:linear-gradient(200deg,rgba(255,255,255,.1),transparent 45%,rgba(0,0,0,.2))}
${S}[dir=rtl] .psp-panel{clip-path:polygon(0 0,100% 0,100% 100%,16% 100%)}
${S}.mb-portrait .psp-panel{width:auto;height:50em;clip-path:polygon(0 0,100% 0,100% 88%,0 100%)}
${S} .psp-pin{position:absolute;inset:0;display:flex;flex-direction:column;padding:2.6em 6em 2.6em 3.2em}
${S}[dir=rtl] .psp-pin{padding:2.6em 3.2em 2.6em 6em}
${S}.mb-portrait .psp-pin{padding:2.6em 3em 7em}

${S} .psp-brand{display:flex;align-items:flex-start;gap:.6em}
${S} .psp-bwords{display:flex;flex-direction:column;align-items:stretch}
${S} .psp-bname{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.2em;line-height:1;
  text-transform:uppercase;letter-spacing:.06em;color:#fff}
${S} .psp-bname.mb-2nd{font-size:1.9em;letter-spacing:0;margin-top:.1em}
${S} .psp-stripe{display:block;height:.42em;margin-top:.5em;width:100%;min-width:10em;background:${STRIPE} 0 0/100% 100% no-repeat;
  border-radius:.1em;box-shadow:0 0 0 .08em rgba(0,0,0,.15)}
${S} .psp-basil{width:4.4em;height:4.4em;flex-shrink:0;margin-top:-.8em;background:${BASIL} center/contain no-repeat;
  filter:drop-shadow(0 .3em .5em rgba(0,0,0,.35))}

${S} .psp-hero{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:1.2em;
  padding-top:1.2em}
${S}.mb-portrait .psp-hero{flex-direction:row-reverse;text-align:start;justify-content:space-between;align-items:center;gap:2.4em}
${S} .psp-photo{position:relative;flex:0 1 auto;min-height:0;height:26em;aspect-ratio:1;max-width:100%}
${S}.mb-portrait .psp-photo{height:31em;flex-shrink:0}
${S} .psp-ph{width:100%;height:100%;border-radius:50%;overflow:hidden;background:var(--mb-surface);
  box-shadow:0 0 0 .5em rgba(255,255,255,.92),0 1.6em 3em rgba(0,0,0,.45)}
${S} .psp-img{width:100%;height:100%;object-fit:cover;display:block}
${S} .psp-initial{display:flex;width:100%;height:100%;align-items:center;justify-content:center;font-size:6em;opacity:.3;
  font-family:var(--mb-font-heading);color:var(--mb-text)}
${S} .psp-badges{position:absolute;top:4%;inset-inline-end:2%;display:flex;flex-direction:column;gap:.3em;align-items:flex-end}
${S} .psp-badge{background:var(--mb-primary);color:var(--mb-bg);font-weight:800;font-size:1em;padding:.3em .8em;border-radius:99em;
  white-space:nowrap;box-shadow:0 .3em .6em rgba(0,0,0,.35)}
${S} .psp-htext{min-width:0;display:flex;flex-direction:column;align-items:center}
${S}.mb-portrait .psp-htext{align-items:flex-start;flex:1}
${S} .psp-hname{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.4em;line-height:1.05;
  text-transform:uppercase;letter-spacing:.04em;color:#fff}
${S} .psp-hname.mb-2nd{font-size:2em;letter-spacing:0}
${S} .psp-hdesc{font-size:1.05em;opacity:.92;margin-top:.4em;max-width:24em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .psp-tagline{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3em;text-transform:uppercase;line-height:1.05}
${S} .psp-tagline.mb-2nd{font-size:2em}
${S} .psp-prices{width:100%;max-width:17em;margin-top:.9em}
${S} .psp-row{display:flex;align-items:baseline;justify-content:space-between;gap:1em;padding:.3em .1em;
  border-bottom:.1em solid rgba(255,255,255,.55)}
${S} .psp-row:last-child{border-bottom:0}
${S} .psp-one{justify-content:center}
${S}.mb-portrait .psp-one{justify-content:flex-start}
${S} .psp-lbl{font-weight:800;font-size:1em;text-transform:uppercase;letter-spacing:.06em;white-space:nowrap}
${S} .psp-amt{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.8em;white-space:nowrap;letter-spacing:.03em}
${S} .psp-chip{display:inline-flex;align-items:baseline;gap:.6em;background:var(--mb-bg);color:var(--mb-primary);padding:.15em .9em;border-radius:.3em;
  transform:rotate(-3deg);box-shadow:0 .5em 1em rgba(0,0,0,.3)}
${S} .psp-chip .psp-amt{font-size:2.4em}
${S} .psp-old{font-size:1.1em;opacity:.6;white-space:nowrap;color:var(--mb-text)}

${S} .psp-main{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;padding:2.8em 3.2em 2.4em 1.4em}
${S}[dir=rtl] .psp-main{padding:2.8em 1.4em 2.4em 3.2em}
${S}.mb-portrait .psp-main{padding:1.2em 3em 2.6em}
${S} .psp-top{display:flex;align-items:baseline;flex-wrap:wrap;gap:0 1em;margin-bottom:2em}
${S} .psp-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.2em;line-height:1;
  text-transform:uppercase;letter-spacing:.06em;color:var(--mb-primary)}
${S} .psp-heading.mb-2nd{font-size:2em;letter-spacing:0}
${S} .psp-rule{flex:1 0 6em;height:.3em;align-self:center;background:${STRIPE} 0 0/100% 100% no-repeat;opacity:.9;border-radius:.1em}
${S} .psp-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:2.6em}
${S}.mb-portrait .psp-cols{grid-template-columns:repeat(2,minmax(0,1fr))}
${S} .psp-col{min-width:0;display:flex;flex-direction:column;gap:1.6em}
${S} .psp-head{margin-bottom:.9em;padding-bottom:.35em;border-bottom:.15em solid var(--mb-accent)}
${S} .psp-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;line-height:1.05;
  text-transform:uppercase;letter-spacing:.06em;color:var(--mb-primary)}
${S} .psp-title.mb-2nd{font-size:1.3em;letter-spacing:0}
${S} .psp-sub{font-family:var(--mb-font-script);font-size:1.1em;opacity:.8;margin-top:.1em}
${listCss(S, 'pspl', { name: '1.5em', price: '1.5em', desc: '.9em', leader: '.12em dotted color-mix(in srgb,var(--mb-text) 35%,transparent)' })}
${S} .pspl-item{margin-bottom:1.2em}
${S} .pspl-name,${S} .pspl-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);text-transform:uppercase;letter-spacing:.04em}
${S} .pspl-price{color:var(--mb-primary)}
${S} .pspl-name2{font-size:1.1em}
${S} .pspl-badge{background:var(--mb-accent);color:#fff}
`,
};
